/* ═══════════════════════════════════════════════════════════════════
 * ww-starter-pack.js
 * v2.0 — controller for the WUNDER Starter Pack PDP.
 *
 * 2026-04-27 — 2-ball + 1-leash architecture:
 *   • Bundle ships 2 WUNDERballs + 1 WUNDERleash at 15% off.
 *   • One ball picker visible by default (covers the dominant
 *     single-dog buyer who wants two of the same).
 *   • "Make them different" toggle reveals a 2nd-ball picker for
 *     multi-dog households / color-variety buyers.
 *   • When toggle is off (or the customer happens to pick identical
 *     variants), ATC adds ONE ball line at qty 2 + ONE leash line.
 *     When toggle is on AND variants differ, ATC adds two ball lines
 *     at qty 1 each + one leash line.
 *   • Bundle pricing fires through a Shopify Automatic Discount
 *     targeting WUNDERball + WUNDERleash with a 3-item minimum and
 *     "Combinations: shipping only" so the leash 20% promo doesn't
 *     double-stack. See STARTER_PACK_DEPLOY-2026-04-27-2ball.md.
 *   • Universal $49 free-ship rule covers every combo automatically
 *     (smallest combo = $51 after 15%; largest = $68). NO bundle-
 *     conditional shipping discount needed.
 *
 * MONSTER UPSELLS CONTRACT (per HANDOFF §4.4 / §6.1):
 *   • Multi-item adds MUST be sequential (one monster_addToCart() at
 *     a time, awaiting each before the next).
 *   • Do NOT call /cart/add.js directly when MU is present.
 *   • Do NOT redirect to /discount/CODE — that causes the "immediately
 *     to checkout" bug from the OG WUNDERbundle. Discount is handled
 *     by Shopify Automatic Discount configured in admin.
 *
 * ════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (!window.SP_DATA) {
    console.warn('[StarterPack] window.SP_DATA missing — section liquid did not inject data');
    return;
  }

  var DATA = window.SP_DATA;
  var SIZE_ORDER = ['Small', 'Medium', 'Large', 'XL'];

  /* ── State ── (initialized via initState() after we know variant values)
     ball  = primary ball (qty 2 by default, qty 1 when mix toggle on)
     ball2 = second ball (only used when mix toggle on; mirrors ball when off)
     mix   = "make them different" toggle state */
  var state = {
    ball:  { size: '', color: '' },
    ball2: { size: '', color: '' },
    leash: { size: '', color: '' },
    mix:   false,
    busy:  false
  };

  /* Smart default resolver: configured default → exact match → fuzzy contains
     match → first available variant value. Handles the case where Shopify
     variant values include extra text like "(MEDIUM) up to 40 lbs." that
     the configured default ("Medium") doesn't match exactly. */
  function resolveDefault(product, axisName, configuredDefault) {
    var optIdx = findOptionIndex(product, [axisName]);
    if (optIdx < 0) return '';
    var values = getValuesForOption(product, optIdx);
    if (!values.length) return '';

    var configLower = (configuredDefault || '').toLowerCase().trim();

    // 1. Exact match
    if (configLower) {
      var exact = values.find(function (v) { return v.toLowerCase() === configLower; });
      if (exact) return exact;
    }

    // 2. Fuzzy contains (e.g. "Medium" matches "(MEDIUM) up to 40 lbs.")
    if (configLower) {
      var fuzzy = values.find(function (v) { return v.toLowerCase().indexOf(configLower) !== -1; });
      if (fuzzy) return fuzzy;
    }

    // 3. First AVAILABLE value (must have at least one available variant)
    var firstAvail = values.find(function (v) {
      return product.variants.some(function (variant) {
        return variant.options[optIdx] === v && variant.available;
      });
    });
    if (firstAvail) return firstAvail;

    // 4. Last resort: first value (regardless of availability)
    return values[0];
  }

  function initState() {
    state.ball.size   = resolveDefault(DATA.ball,  'size',           DATA.defaults.ball_size);
    state.ball.color  = resolveDefault(DATA.ball,  'color',          DATA.defaults.ball_color);
    state.leash.size  = resolveDefault(DATA.leash, 'size',           DATA.defaults.leash_size);
    state.leash.color = resolveDefault(DATA.leash, 'color',          DATA.defaults.leash_color);

    // If the resolved combination still has no available variant (e.g. the
    // resolved color is OOS for the resolved size), nudge to a known-good
    // combination by walking variants for the first ball variant that's
    // available, then matching.
    var ballSizeIdx  = findOptionIndex(DATA.ball, ['size']);
    var ballColorIdx = findOptionIndex(DATA.ball, ['color', 'colour']);
    var ballMatch = findVariant(DATA.ball, ballSizeIdx, state.ball.size, ballColorIdx, state.ball.color);
    if (!ballMatch || !ballMatch.available) {
      var firstAvailBall = DATA.ball.variants.find(function (v) { return v.available; });
      if (firstAvailBall) {
        if (ballSizeIdx >= 0)  state.ball.size  = firstAvailBall.options[ballSizeIdx]  || state.ball.size;
        if (ballColorIdx >= 0) state.ball.color = firstAvailBall.options[ballColorIdx] || state.ball.color;
      }
    }

    // Ball #2 starts as a mirror of ball #1. Toggling the mix flag does NOT
    // reset it — once the customer differentiates, their choice persists.
    state.ball2.size  = state.ball.size;
    state.ball2.color = state.ball.color;

    var leashSizeIdx  = findOptionIndex(DATA.leash, ['size']);
    var leashColorIdx = findOptionIndex(DATA.leash, ['color', 'colour']);
    var leashMatch = findVariant(DATA.leash, leashSizeIdx, state.leash.size, leashColorIdx, state.leash.color);
    if (!leashMatch || !leashMatch.available) {
      var firstAvailLeash = DATA.leash.variants.find(function (v) { return v.available; });
      if (firstAvailLeash) {
        if (leashSizeIdx >= 0)  state.leash.size  = firstAvailLeash.options[leashSizeIdx]  || state.leash.size;
        if (leashColorIdx >= 0) state.leash.color = firstAvailLeash.options[leashColorIdx] || state.leash.color;
      }
    }
  }

  /* ── Utilities ── */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function money(cents) {
    var n = Number(cents || 0) / 100;
    return '$' + n.toFixed(2);
  }

  function findOptionIndex(product, namePattern) {
    for (var i = 0; i < product.options.length; i++) {
      var lower = product.options[i].name.toLowerCase();
      for (var j = 0; j < namePattern.length; j++) {
        if (lower.indexOf(namePattern[j]) !== -1) return i;
      }
    }
    return -1;
  }

  function getValuesForOption(product, optIndex) {
    if (optIndex < 0) return [];
    return product.options[optIndex].values || [];
  }

  function findVariant(product, sizeOptIdx, sizeVal, colorOptIdx, colorVal) {
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var matchSize = sizeOptIdx < 0 || v.options[sizeOptIdx] === sizeVal;
      var matchColor = colorOptIdx < 0 || v.options[colorOptIdx] === colorVal;
      if (matchSize && matchColor) return v;
    }
    return null;
  }

  function sortSizes(values) {
    /* Sort by canonical Small → Medium → Large → XL order using substring
       match so values like "(MEDIUM) up to 40 lbs." sort with "Medium". */
    var out = [];
    SIZE_ORDER.forEach(function (s) {
      var sLower = s.toLowerCase();
      var match = values.find(function (v) {
        return v.toLowerCase().indexOf(sLower) !== -1;
      });
      if (match && out.indexOf(match) === -1) out.push(match);
    });
    // Append any value not in canonical order (defensive — covers special editions, custom sizes)
    values.forEach(function (v) {
      if (out.indexOf(v) === -1) out.push(v);
    });
    return out;
  }

  /* ── Compute current selection ── */
  function getCurrentBallVariant() {
    var ballSizeIdx  = findOptionIndex(DATA.ball, ['size']);
    var ballColorIdx = findOptionIndex(DATA.ball, ['color', 'colour']);
    return findVariant(DATA.ball, ballSizeIdx, state.ball.size, ballColorIdx, state.ball.color);
  }

  function getCurrentBall2Variant() {
    // When mix toggle is off, ball2 is by definition a copy of ball1.
    if (!state.mix) return getCurrentBallVariant();
    var ballSizeIdx  = findOptionIndex(DATA.ball, ['size']);
    var ballColorIdx = findOptionIndex(DATA.ball, ['color', 'colour']);
    return findVariant(DATA.ball, ballSizeIdx, state.ball2.size, ballColorIdx, state.ball2.color);
  }

  function getCurrentLeashVariant() {
    var leashSizeIdx  = findOptionIndex(DATA.leash, ['size']);
    var leashColorIdx = findOptionIndex(DATA.leash, ['color', 'colour']);
    return findVariant(DATA.leash, leashSizeIdx, state.leash.size, leashColorIdx, state.leash.color);
  }

  /* ── Pricing ──
     2026-04-27: 2 balls + 1 leash, Mirror OG pattern (Automatic Discount)
     ─────────────────────────────────────────────────────────────────
     CRITICAL: this function MUST produce the exact same number Shopify
     produces in the cart. PDP price = cart price is non-negotiable
     (CLAUDE.md "THE BAR"). Any drift here is a brand-grade defect.

     How Shopify calculates "Amount off products" percentage discounts
     on a multi-line cart: per-line-item floor, then sum. With 2 balls
     and 1 leash:
       • If both balls are the SAME variant → cart has 1 ball line at
         qty 2 + 1 leash line. Per-line floor on each:
           ball_discount  = floor(ball.price  × qty × rate)
                          ≈ qty × floor(ball.price × rate)
         Shopify actually applies the % to the line total then floors
         at the line level. Since qty is an integer multiplier and rate
         is a constant, floor(qty * price * rate) === qty * floor(price
         * rate) for any rate where price * rate doesn't introduce a
         fractional part above the line-floor boundary. With 15% it's
         identical for every variant price ending in even cents.
         Defensive math: we compute it as qty × floor(price × rate)
         which is what Shopify shows in the cart line breakdown.
       • If the balls are DIFFERENT variants → cart has 2 separate ball
         lines + 1 leash line. Per-line floor on each independently.

     We mirror that here. Aggregate floor (floor(sum × (1−rate))) drifts
     by 1–2 cents on certain combos — DO NOT switch to that.
     ──────────────────────────────────────────────────────────────── */
  function calcPricing() {
    var b1 = getCurrentBallVariant();
    var b2 = getCurrentBall2Variant();
    var lv = getCurrentLeashVariant();

    if (!b1 || !b2 || !lv) {
      // complete:false means "not all 3 picks resolved to a real variant yet."
      // renderPricing uses this to keep the price hero in its initial
      // "Starting at {floor}" state instead of flipping to "Sold out."
      return { total: 0, was: 0, save: 0, perInstallment: 0, available: false, complete: false };
    }

    var rate = DATA.bundle.discount_rate; // e.g. 0.15 for 15% off

    // Per-line-item floor — matches Shopify's exact arithmetic.
    // Two balls treated independently to handle the mix case correctly.
    var ball1Discount = Math.floor(b1.price * rate);
    var ball2Discount = Math.floor(b2.price * rate);
    var leashDiscount = Math.floor(lv.price * rate);

    var was   = b1.price + b2.price + lv.price;
    var save  = ball1Discount + ball2Discount + leashDiscount;
    var total = was - save;

    return {
      total: total,
      was: was,
      save: save,
      ball1Discount: ball1Discount,
      ball2Discount: ball2Discount,
      leashDiscount: leashDiscount,
      perInstallment: Math.round(total / 4),
      available: b1.available && b2.available && lv.available,
      ballVariant:  b1,
      ball2Variant: b2,
      leashVariant: lv,
      qualifiesFreeShip: total >= (DATA.bundle.free_ship_threshold || 4900),
      complete: true
    };
  }

  function renderPricing() {
    var p = calcPricing();
    var nowEl    = $('#sp-price-now');
    var wasEl    = $('#sp-price-was');
    var saveEl   = $('#sp-price-save');
    var labelEl  = $('#sp-price-label');
    var totalEl  = $('#sp-commit-total');
    var commitWasEl = $('#sp-commit-was');
    var savePill = $('#sp-commit-save');
    var shipEl   = $('#sp-commit-ship');
    var shipTxt  = $('#sp-commit-ship-text');
    var stickyP  = $('#sp-sticky-price');
    var atcLabel = $('#sp-atc-label');

    /* Branch 1: customer hasn't completed all 3 picks yet.
       Leave the price hero in its initial "Starting at {floor}" state
       (the Liquid template seeded it from product.price). The commit
       zone and sticky still show "—" because the customer can't ATC
       until everything is picked. This is the Apple/Tesla pattern:
       the floor is honest, the configurator is obvious, no fake $0. */
    if (!p.complete) {
      if (labelEl) {
        labelEl.textContent = 'Starting at';
        labelEl.setAttribute('data-state', 'initial');
      }
      // Don't touch nowEl/wasEl/saveEl — Liquid initialized them to floor.
      if (totalEl) totalEl.textContent = '—';
      if (commitWasEl) commitWasEl.classList.add('hidden');
      if (savePill) savePill.classList.add('hidden');
      if (stickyP) stickyP.textContent = 'Pick your pack';
      if (atcLabel) atcLabel.textContent = 'Pick your pack';
      var atcBtnInit = $('#sp-atc');
      var stkBtnInit = $('#sp-sticky-atc');
      if (atcBtnInit) atcBtnInit.classList.add('is-disabled');
      if (stkBtnInit) stkBtnInit.classList.add('is-disabled');
      return;
    }

    /* Branch 2: complete selection but a chosen variant is out of stock. */
    if (!p.available) {
      if (labelEl) {
        labelEl.textContent = 'Sold out';
        labelEl.setAttribute('data-state', 'unavailable');
      }
      if (nowEl) nowEl.textContent = '—';
      if (wasEl) wasEl.classList.add('hidden');
      if (saveEl) saveEl.classList.add('hidden');
      if (totalEl) totalEl.textContent = '—';
      if (commitWasEl) commitWasEl.classList.add('hidden');
      if (savePill) savePill.classList.add('hidden');
      if (stickyP) stickyP.textContent = 'Sold out';
      if (atcLabel) atcLabel.textContent = 'Out of Stock';
      var atcBtn = $('#sp-atc');
      var stkBtn = $('#sp-sticky-atc');
      if (atcBtn) atcBtn.classList.add('is-disabled');
      if (stkBtn) stkBtn.classList.add('is-disabled');
      return;
    }

    /* Branch 3: complete + available — flip eyebrow to "Your pack"
       and let the existing render logic show the calculated total. */
    if (labelEl) {
      labelEl.textContent = 'Your pack';
      labelEl.setAttribute('data-state', 'configured');
    }

    /* Price hero (top of buy module) */
    if (nowEl) nowEl.textContent = money(p.total);
    if (wasEl) {
      if (p.save > 0) {
        wasEl.textContent = money(p.was);
        wasEl.classList.remove('hidden');
        wasEl.setAttribute('aria-hidden', 'false');
      } else {
        wasEl.classList.add('hidden');
      }
    }
    if (saveEl) {
      if (p.save > 0) {
        saveEl.textContent = 'Save ' + money(p.save);
        saveEl.classList.remove('hidden');
        saveEl.setAttribute('aria-hidden', 'false');
      } else {
        saveEl.classList.add('hidden');
      }
    }

    /* Commit zone (bottom of buy module — locked design 2026-04-27) */
    if (totalEl) totalEl.textContent = money(p.total);
    if (commitWasEl) {
      if (p.save > 0) {
        commitWasEl.textContent = money(p.was);
        commitWasEl.classList.remove('hidden');
      } else {
        commitWasEl.classList.add('hidden');
      }
    }
    if (savePill) {
      if (p.save > 0) {
        savePill.textContent = 'Save ' + money(p.save);
        savePill.classList.remove('hidden');
      } else {
        savePill.classList.add('hidden');
      }
    }

    /* Dynamic shipping line — borrowed from OG WUNDERbundle pattern.
       Two states: under threshold → "Add $X.XX more for free shipping"
                   at/above        → "✓ Free shipping unlocked" (green)
       The data-state attribute drives CSS color/icon swaps. */
    var threshold = (DATA.bundle && DATA.bundle.free_ship_threshold) || 4900;
    if (shipEl && shipTxt) {
      if (p.total >= threshold) {
        shipEl.dataset.state = 'unlocked';
        shipTxt.innerHTML = '<strong>&#x2713; Free shipping unlocked</strong>';
      } else {
        shipEl.dataset.state = 'under';
        var remaining = threshold - p.total;
        shipTxt.innerHTML = 'Add <strong>' + money(remaining) + '</strong> more for free shipping';
      }
    }

    if (stickyP) {
      stickyP.textContent = p.total >= threshold
        ? money(p.total) + ' · Free ship'
        : money(p.total) + ' + $7.95 ship';
    }
    if (atcLabel) atcLabel.textContent = 'Add Starter Pack to Cart — ' + money(p.total);

    var atcBtn = $('#sp-atc');
    var stkBtn = $('#sp-sticky-atc');
    if (atcBtn) atcBtn.classList.remove('is-disabled');
    if (stkBtn) stkBtn.classList.remove('is-disabled');
  }

  /* ── Picker rendering ── */
  /* Parse a Shopify variant value into { label, sub } so the chip can
     show "Small" with "Up to 15 lbs" underneath instead of one verbose
     line. Handles three common patterns:
       "(SMALL) up to 15 lbs."     → { label: "Small", sub: "Up to 15 lbs" }
       "Small / Up to 60 lbs"      → { label: "Small", sub: "Up to 60 lbs" }
       "Small"                      → { label: "Small", sub: "" }
  */
  function parseSizeLabel(value) {
    if (!value) return { label: '', sub: '' };

    // Pattern 1: "(SIZE) descriptor"  e.g. "(SMALL) up to 15 lbs."
    var paren = value.match(/^\(([^)]+)\)\s*(.*)$/);
    if (paren) {
      var primaryParen = paren[1];                        // "SMALL"
      var subParen = (paren[2] || '').replace(/\.$/, ''); // "up to 15 lbs"
      return {
        label: primaryParen.charAt(0).toUpperCase() + primaryParen.slice(1).toLowerCase(),
        sub:   subParen.charAt(0).toUpperCase() + subParen.slice(1)
      };
    }

    // Pattern 2: "Size / weight"  e.g. "Small / Up to 60 lbs"
    var slash = value.match(/^([^\/]+)\/(.+)$/);
    if (slash) {
      return {
        label: slash[1].trim(),
        sub:   slash[2].trim()
      };
    }

    // Pattern 3: "Size: weight"  e.g. "Small: up to 60 lbs"
    var colon = value.match(/^([^:]+):\s*(.+)$/);
    if (colon) {
      return {
        label: colon[1].trim(),
        sub:   (colon[2].trim().charAt(0).toUpperCase() + colon[2].trim().slice(1))
      };
    }

    // Pattern 4: just "Small" — no subtitle
    return { label: value, sub: '' };
  }

  function renderSizeChips(container, values, currentVal, onSelect, available) {
    container.innerHTML = '';
    sortSizes(values).forEach(function (val) {
      var parsed = parseSizeLabel(val);
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'sp-chip';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('aria-checked', val === currentVal ? 'true' : 'false');
      chip.dataset.value = val;
      // Two-line chip: primary label + weight subtitle (matches leash .ww-size-btn pattern)
      var labelHtml = '<span class="sp-chip-label">' + escapeHtml(parsed.label) + '</span>';
      var subHtml = parsed.sub ? '<span class="sp-chip-sub">' + escapeHtml(parsed.sub) + '</span>' : '';
      chip.innerHTML = labelHtml + subHtml;
      if (val === currentVal) chip.classList.add('active');
      if (available && available[val] === false) {
        chip.classList.add('is-unavailable');
        chip.setAttribute('aria-disabled', 'true');
      }
      chip.addEventListener('click', function () { onSelect(val); });
      container.appendChild(chip);
    });
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  /* ════════════════════════════════════════════════════════════════
     CANONICAL SITE-WIDE SWATCH PALETTE
     Mirrored verbatim in:
       • ww-wunderball.css   (--swatch-* CSS variables)
       • ww-pdp.css / ww-main-product.liquid  (inline {%- when -%} cases)
     If a value changes here it MUST change in those files too.
     See CLAUDE.md "Picker Standard" for governance.
     ════════════════════════════════════════════════════════════════ */
  var COLOR_HEX = {
    'swirl':   'conic-gradient(from 45deg, #E8622A, #F0287A, #7B3FF2, #00AAEC, #3BB44A, #E8622A)',
    'orange':  '#E8622A',
    'green':   '#3BB44A',
    'pink':    '#F0287A',
    'purple':  '#7B3FF2',
    'blue':    '#00AAEC',
    'blueberry': '#00AAEC',
    'yellow':  '#F6EC44',
    'red':     '#EC1C24',
    'black':   '#1A1A18',
    'silver':  '#ABABAB',
    'white':   '#FFFFFF',
    'tan':     '#C9A97A',
    'brown':   '#7D5A3C',
    'navy':    '#1F2A4A',
    'gray':    '#8a8a88',
    'grey':    '#8a8a88'
  };

  function colorSwatchStyle(name) {
    var key = (name || '').toLowerCase().trim();
    var val = COLOR_HEX[key];
    if (!val) {
      // fallback: try first match against the start of the name
      for (var k in COLOR_HEX) {
        if (key.indexOf(k) !== -1) { val = COLOR_HEX[k]; break; }
      }
    }
    if (!val) val = '#cccccc';
    return val.indexOf('gradient') !== -1
      ? 'background-image:' + val
      : 'background-color:' + val;
  }

  function renderSwatches(container, values, currentVal, onSelect, available) {
    container.innerHTML = '';
    values.forEach(function (val) {
      var sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'sp-swatch';
      sw.setAttribute('role', 'radio');
      sw.setAttribute('aria-checked', val === currentVal ? 'true' : 'false');
      sw.setAttribute('aria-label', val);
      sw.dataset.value = val;
      sw.style.cssText = colorSwatchStyle(val);
      if (val === currentVal) sw.classList.add('active');
      if (available && available[val] === false) {
        sw.classList.add('is-unavailable');
        sw.setAttribute('aria-disabled', 'true');
      }
      var dot = document.createElement('span');
      dot.className = 'sp-swatch-dot';
      sw.appendChild(dot);
      sw.addEventListener('click', function () { onSelect(val); });
      container.appendChild(sw);
    });
  }

  /* ── Availability lookup: which values are in stock for current other selections? ── */
  function buildAvailabilityMap(product, axis, otherAxis, otherVal) {
    var axisIdx  = findOptionIndex(product, [axis]);
    var otherIdx = findOptionIndex(product, [otherAxis]);
    var values = getValuesForOption(product, axisIdx);
    var map = {};
    values.forEach(function (v) {
      var found = product.variants.some(function (variant) {
        var matchAxis = variant.options[axisIdx] === v;
        var matchOther = otherIdx < 0 || variant.options[otherIdx] === otherVal;
        return matchAxis && matchOther && variant.available;
      });
      map[v] = found;
    });
    return map;
  }

  /* ── Wire up pickers ── */
  function rerenderAllPickers() {
    var ballSizes = getValuesForOption(DATA.ball, findOptionIndex(DATA.ball, ['size']));
    var ballColors = getValuesForOption(DATA.ball, findOptionIndex(DATA.ball, ['color', 'colour']));
    var leashSizes = getValuesForOption(DATA.leash, findOptionIndex(DATA.leash, ['size']));
    var leashColors = getValuesForOption(DATA.leash, findOptionIndex(DATA.leash, ['color', 'colour']));

    renderSizeChips(
      $('[data-picker="ball-size"] .sp-picker-chips'),
      ballSizes, state.ball.size,
      function (v) {
        state.ball.size = v;
        // Mirror to ball2 only when toggle is OFF (toggle on = independent state)
        if (!state.mix) state.ball2.size = v;
        updateAll();
      },
      buildAvailabilityMap(DATA.ball, 'size', 'color', state.ball.color)
    );

    renderSwatches(
      $('[data-picker="ball-color"] .sp-picker-swatches'),
      ballColors, state.ball.color,
      function (v) {
        state.ball.color = v;
        if (!state.mix) state.ball2.color = v;
        updateAll();
      },
      buildAvailabilityMap(DATA.ball, 'color', 'size', state.ball.size)
    );

    /* Ball #2 pickers — same source data, independent target state */
    var ball2SizeContainer = $('[data-picker="ball2-size"] .sp-picker-chips');
    if (ball2SizeContainer) {
      renderSizeChips(
        ball2SizeContainer,
        ballSizes, state.ball2.size,
        function (v) { state.ball2.size = v; updateAll(); },
        buildAvailabilityMap(DATA.ball, 'size', 'color', state.ball2.color)
      );
    }
    var ball2ColorContainer = $('[data-picker="ball2-color"] .sp-picker-swatches');
    if (ball2ColorContainer) {
      renderSwatches(
        ball2ColorContainer,
        ballColors, state.ball2.color,
        function (v) { state.ball2.color = v; updateAll(); },
        buildAvailabilityMap(DATA.ball, 'color', 'size', state.ball2.size)
      );
    }

    renderSizeChips(
      $('[data-picker="leash-size"] .sp-picker-chips'),
      leashSizes, state.leash.size,
      function (v) { state.leash.size = v; updateAll(); },
      buildAvailabilityMap(DATA.leash, 'size', 'color', state.leash.color)
    );

    renderSwatches(
      $('[data-picker="leash-color"] .sp-picker-swatches'),
      leashColors, state.leash.color,
      function (v) { state.leash.color = v; updateAll(); },
      buildAvailabilityMap(DATA.leash, 'color', 'size', state.leash.size)
    );

    /* Selection echoes — written in 3 places, all derived from the same state:
         1. .sp-picker-label-current (right-aligned next to "PICK A SIZE/COLOR")
         2. The commit zone selection line(s)
       The active chip / active swatch are the primary visual indicator. The
       text echoes are confirmations, not duplicates — they're the
       "I scanned to verify" UX safety net. All update from one render pass. */
    var ballSizeParsed  = parseSizeLabel(state.ball.size);
    var ball2SizeParsed = parseSizeLabel(state.ball2.size);
    var leashSizeParsed = parseSizeLabel(state.leash.size);
    var ballSizeLabel   = ballSizeParsed.label  || state.ball.size;
    var ball2SizeLabel  = ball2SizeParsed.label || state.ball2.size;
    var leashSizeLabel  = leashSizeParsed.label || state.leash.size;

    /* Per-picker label echoes */
    function setCurrent(pickerSelector, axis, value) {
      var grp = $(pickerSelector);
      if (!grp) return;
      var slot = grp.querySelector('[data-current-for="' + axis + '"]');
      if (slot) slot.textContent = value;
    }
    setCurrent('[data-picker="ball-size"]',   'size',  ballSizeLabel);
    setCurrent('[data-picker="ball-color"]',  'color', state.ball.color);
    setCurrent('[data-picker="ball2-size"]',  'size',  ball2SizeLabel);
    setCurrent('[data-picker="ball2-color"]', 'color', state.ball2.color);
    setCurrent('[data-picker="leash-size"]',  'size',  leashSizeLabel);
    setCurrent('[data-picker="leash-color"]', 'color', state.leash.color);

    /* Commit zone selection summary —
       When mix is OFF or balls happen to be identical: "2× <ball variant>"
       When mix is ON and balls differ: "1× <ball #1> + 1× <ball #2>"
       The leash always shows as "1× <leash variant>". */
    var ballsIdentical = (state.ball.size === state.ball2.size) &&
                         (state.ball.color === state.ball2.color);
    var showSplit = state.mix && !ballsIdentical;

    var selBall  = $('#sp-sel-ball');
    var selBall2Line = $('#sp-sel-ball2-line');
    var selBall2 = $('#sp-sel-ball2');
    var ballPlus = $('.sp-commit-selection-line:not(.sp-commit-selection-line--ball2)');
    var lineQty  = showSplit ? '1×' : '2×';

    /* The first line element's leading "2×" / "1×" is rendered server-side.
       Replace it without touching the inner <strong>. We do this by editing
       the child text node before the strong. Simple enough — the server
       template provides "2× <strong>...</strong>" so child node 0 is text. */
    var firstLine = $('.sp-commit-selection-line:not(.sp-commit-selection-line--ball2)');
    if (firstLine && firstLine.firstChild && firstLine.firstChild.nodeType === 3) {
      firstLine.firstChild.nodeValue = lineQty + ' ';
    }
    if (selBall)  selBall.textContent  = ballSizeLabel  + ' ' + state.ball.color  + ' WUNDERball';
    if (selBall2) selBall2.textContent = ball2SizeLabel + ' ' + state.ball2.color + ' WUNDERball';
    if (selBall2Line) {
      if (showSplit) {
        selBall2Line.removeAttribute('hidden');
      } else {
        selBall2Line.setAttribute('hidden', '');
      }
    }
    var selLeash = $('#sp-sel-leash');
    if (selLeash) selLeash.textContent = leashSizeLabel + ' ' + state.leash.color + ' WUNDERleash';
  }


  function updateAll() {
    rerenderAllPickers();
    renderPricing();
  }

  /* ── Add to Cart ── */
  function setStatus(msg, isError) {
    var el = $('#sp-status');
    if (!el) return;
    el.textContent = msg || '';
    el.className = 'sp-status' + (isError ? ' is-error' : '');
  }

  function addStarterPackToCart(opts) {
    opts = opts || {};
    if (state.busy) return;
    var p = calcPricing();
    if (!p.available) {
      setStatus('That combination is out of stock. Try another size or color.', true);
      return;
    }

    state.busy = true;
    var atcBtn = $('#sp-atc');
    var stkBtn = $('#sp-sticky-atc');
    var atcLabel = $('#sp-atc-label');
    var origLabel = atcLabel ? atcLabel.textContent : 'Add to Cart';
    if (atcLabel) atcLabel.textContent = 'Adding…';
    if (atcBtn) atcBtn.classList.add('is-busy');
    if (stkBtn) stkBtn.classList.add('is-busy');
    setStatus('');

    /* 2026-04-27 — 2-ball + 1-leash bundle, Mirror OG pattern:
       Cart receives REAL line items only (no $0 shell anchor). Each
       line is tagged with the bundle property so the Shopify Automatic
       Discount can target them. When both balls are identical variants,
       coalesce into a single qty-2 line for cleaner cart UX (matches
       customer's mental model: "two of these"). When the customer has
       toggled "make them different" AND chosen different variants, add
       two separate qty-1 ball lines so each renders with its own
       size/color in the cart. The leash is always its own line. */
    var properties = { '_bundle': DATA.bundle.bundle_tag };
    var ballsIdentical = p.ballVariant.id === p.ball2Variant.id;
    var items = [];
    if (ballsIdentical) {
      items.push({ id: p.ballVariant.id, quantity: 2, properties: properties });
    } else {
      items.push({ id: p.ballVariant.id,  quantity: 1, properties: properties });
      items.push({ id: p.ball2Variant.id, quantity: 1, properties: properties });
    }
    items.push({ id: p.leashVariant.id, quantity: 1, properties: properties });

    /* Path 1: Monster Upsells sequential chain (preferred) */
    var muAdd = window.monster_addToCart || window.monster_atc;
    if (typeof muAdd === 'function') {
      var queue = items.slice();
      function next() {
        if (!queue.length) {
          // All items added.
          // If the caller asked for a redirect (Shop Pay express flow),
          // honor it — items are already in cart, customer goes straight
          // to /checkout where Shop Pay's express handler picks up.
          // Otherwise the MU drawer handles the UX (drawer stays open,
          // customer chooses Checkout vs Continue Shopping).
          state.busy = false;
          if (opts.thenRedirect) {
            if (atcLabel) atcLabel.textContent = 'Going to checkout…';
            window.location.href = opts.thenRedirect;
            return;
          }
          if (atcLabel) atcLabel.textContent = '✓ Added to Cart';
          setTimeout(function () {
            if (atcLabel) atcLabel.textContent = origLabel;
            if (atcBtn) atcBtn.classList.remove('is-busy');
            if (stkBtn) stkBtn.classList.remove('is-busy');
          }, 2400);
          return;
        }
        var item = queue.shift();
        try {
          var ret = muAdd(item);
          if (ret && typeof ret.then === 'function') {
            ret.then(next).catch(function (err) { handleAtcError(err); });
          } else {
            // MU may not return a promise; give it a beat to update DOM
            setTimeout(next, 350);
          }
        } catch (e) {
          handleAtcError(e);
        }
      }
      next();
      return;
    }

    /* Path 2: Fallback /cart/add.js — refresh cart drawer via Section Rendering API */
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: 'cart-drawer,cart-notification,cart-icon-bubble,cart'
      })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Cart add failed (' + r.status + ')');
      return r.json();
    })
    .then(function (resp) {
      state.busy = false;
      // Same redirect semantics as the MU path — Shop Pay flow asks for
      // /checkout; standard ATC opens the drawer with the items inside.
      if (opts.thenRedirect) {
        if (atcLabel) atcLabel.textContent = 'Going to checkout…';
        window.location.href = opts.thenRedirect;
        return;
      }
      injectCartSections(resp.sections || {});
      openCartDrawer();
      if (atcLabel) atcLabel.textContent = '✓ Added to Cart';
      setTimeout(function () {
        if (atcLabel) atcLabel.textContent = origLabel;
        if (atcBtn) atcBtn.classList.remove('is-busy');
        if (stkBtn) stkBtn.classList.remove('is-busy');
      }, 2400);
    })
    .catch(handleAtcError);
  }

  function handleAtcError(err) {
    console.error('[StarterPack] ATC error:', err);
    state.busy = false;
    var atcBtn = $('#sp-atc');
    var stkBtn = $('#sp-sticky-atc');
    var atcLabel = $('#sp-atc-label');
    if (atcLabel) atcLabel.textContent = 'Try again';
    if (atcBtn) atcBtn.classList.remove('is-busy');
    if (stkBtn) stkBtn.classList.remove('is-busy');
    setStatus('Something went wrong adding to cart. Please try again or refresh the page.', true);
  }

  function injectCartSections(sections) {
    Object.keys(sections).forEach(function (key) {
      var el = document.querySelector('#shopify-section-' + key) ||
               document.querySelector('[data-section-id="' + key + '"]') ||
               document.querySelector('cart-drawer');
      if (el && sections[key]) el.innerHTML = sections[key];
    });
  }

  function openCartDrawer() {
    var dr = document.querySelector('cart-drawer');
    if (dr && typeof dr.open === 'function') { dr.open(); return; }
    var btn = document.querySelector('[aria-controls="cart-drawer"], [data-cart-drawer-toggle]');
    if (btn && typeof btn.click === 'function') { btn.click(); return; }
  }

  /* ── Gallery (mirror WUNDERball pattern: thumbs toggle .active on slides) ── */
  function activateThumb(targetIdx) {
    $$('.sp-g-thumb').forEach(function (t) {
      var match = String(t.dataset.target) === String(targetIdx);
      t.classList.toggle('active', match);
      t.setAttribute('aria-selected', match ? 'true' : 'false');
    });
    $$('.sp-g-main-slide').forEach(function (s) {
      s.classList.toggle('active', String(s.dataset.slide) === String(targetIdx));
    });
  }

  function bindGallery() {
    $$('.sp-g-thumb').forEach(function (t) {
      t.addEventListener('click', function () { activateThumb(t.dataset.target); });
      t.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activateThumb(t.dataset.target);
        }
      });
    });
  }

  /* ── Sticky bar visibility (matches WUNDERball pattern) ── */
  function bindSticky() {
    var sticky = $('#sp-sticky');
    var commit = $('#sp-commit');
    if (!sticky || !commit) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Sticky shows when commit zone is OUT of viewport
        sticky.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { rootMargin: '0px 0px -100px 0px', threshold: 0 });
    observer.observe(commit);
  }

  /* ── "Make them different" toggle ──
     When toggled ON: state.mix = true. The 2nd-ball picker becomes
       visible, ball2 selections are independent of ball1, and the
       step #1 title swaps from "Your WUNDERballs (qty 2)" to "Your
       WUNDERball" (singular, no qty badge — because that card now
       represents one ball, with the second in its own card).
     When toggled OFF: state.mix = false. The 2nd-ball picker hides,
       ball2 is forced to mirror ball1, and the step #1 title goes
       back to plural with the qty 2 badge. */
  function syncMixUX() {
    var step      = $('#sp-step-ball2');
    var qtyBadge  = $('#sp-step-qty-badge');
    var noun      = $('.sp-step-title-noun');
    if (!step) return;
    if (state.mix) {
      step.removeAttribute('hidden');
      step.setAttribute('aria-hidden', 'false');
      if (qtyBadge) qtyBadge.style.display = 'none';
      if (noun) noun.textContent = noun.dataset.nounSingular || 'WUNDERball';
    } else {
      step.setAttribute('hidden', '');
      step.setAttribute('aria-hidden', 'true');
      if (qtyBadge) qtyBadge.style.display = '';
      if (noun) noun.textContent = noun.dataset.nounPlural || 'WUNDERballs';
    }
  }

  function bindMixToggle() {
    var input = $('#sp-mix-toggle-input');
    if (!input) return;
    input.addEventListener('change', function () {
      state.mix = !!input.checked;
      if (!state.mix) {
        // Resync ball2 to ball1 so both balls match again
        state.ball2.size  = state.ball.size;
        state.ball2.color = state.ball.color;
      }
      syncMixUX();
      updateAll();
    });
    // Sync UX on init in case state.mix is true from saved state
    syncMixUX();
  }

  /* ── Shop Pay button intercept ──
     The Liquid template renders {{ form | payment_button }} inside a
     <form> for the WUNDER Starter Pack product. By default that submits
     the shell variant only — useless for our 2-ball + 1-leash bundle.
     We intercept the click, run the same multi-item ATC flow that the
     primary ATC button uses, then route the customer to /checkout
     where Shop Pay's express flow takes over with the real items in cart. */
  function bindShopPayIntercept() {
    var wrap = $('#sp-shoppay-wrap');
    if (!wrap) return;
    /* The dynamic checkout button renders inside the wrap as a Web
       Component or a styled <button>. We bind on the wrap's click —
       capture phase so we run before Shopify's handler dispatches the
       form submit. */
    wrap.addEventListener('click', function (e) {
      if (state.busy) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var p = calcPricing();
      if (!p.available) {
        e.preventDefault();
        e.stopPropagation();
        setStatus('That combination is out of stock. Try another size or color.', true);
        return;
      }
      // Stop the form's default submit (which would express-checkout the shell variant)
      e.preventDefault();
      e.stopPropagation();
      // Run the multi-item ATC, then route to /checkout where Shop Pay express picks up
      addStarterPackToCart({ thenRedirect: '/checkout' });
    }, true);
  }

  /* ── Init ── */
  function init() {
    initState();           // Resolve defaults against actual variant values + availability
    updateAll();
    bindGallery();
    bindSticky();
    bindMixToggle();
    bindShopPayIntercept();

    var atcBtn = $('#sp-atc');
    var stkBtn = $('#sp-sticky-atc');
    if (atcBtn) atcBtn.addEventListener('click', function () { addStarterPackToCart(); });
    if (stkBtn) stkBtn.addEventListener('click', function () { addStarterPackToCart(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
